import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl as MUIFormControl, FormHelperText as MUIFormHelperText, InputLabel as MUIInputLabel, MenuItem as MUIMenuItem, Select as MUISelect, styled, Typography, } from "@mui/material";
import { convertPxToRem } from "../../util-functions";
import { Icon } from "../Icon";
import { lightGreyColors } from "../TreezThemeProvider";
const StyledFormControl = styled(MUIFormControl)(({ theme }) => ({
    "&:hover": {
        label: {
            color: theme.palette.primaryBlack.main,
        },
    },
    // MUI dropdown icon
    svg: {
        color: theme.palette.primaryBlack.main,
        right: 16,
    },
}));
const StyledInputLabel = styled(MUIInputLabel, {
    shouldForwardProp: (prop) => prop !== "small",
})(({ theme, small }) => ({
    "&.MuiInputLabel-root": {
        color: theme.palette.text.secondary,
        left: convertPxToRem(4),
        "&[data-shrink=true]": Object.assign({}, theme.typography.smallText),
        "&[data-shrink=false]": Object.assign(Object.assign({}, theme.typography.largeText), { top: small ? convertPxToRem(-3) : 0 }),
        "&.Mui-disabled": {
            color: theme.palette.tertiaryText.main,
        },
    },
}));
const StyledSelect = styled(MUISelect, {
    shouldForwardProp: (prop) => prop !== "small",
})(({ theme, small }) => ({
    height: convertPxToRem(small ? 40 : 52),
    backgroundColor: theme.palette.treezGrey[3],
    borderRadius: convertPxToRem(19),
    padding: 0,
    // keeps the invisible input field positioned correctly
    display: "flex",
    alignItems: "end",
    "&:hover": {
        backgroundColor: theme.palette.treezGrey[3],
    },
    "&:focus": {
        outline: `${convertPxToRem(2.5)} solid ${theme.palette.primaryBlack.main}`,
    },
    "&.Mui-disabled": {
        backgroundColor: theme.palette.treezGrey[2],
        outline: "unset",
    },
    "& .MuiFilledInput-input": {
        paddingLeft: convertPxToRem(16),
        paddingTop: convertPxToRem(small ? 13 : 21),
        maxHeight: convertPxToRem(small ? 40 : 52),
        "&:focus": {
            backgroundColor: "transparent",
            borderRadius: convertPxToRem(19),
            outline: `${convertPxToRem(2.5)} solid ${theme.palette.primaryBlack.main}`,
            // adjust the padding on focus to make focused input appear the same as the when the select is focused
            paddingTop: convertPxToRem(small ? 13 : 21),
        },
    },
    //  remove gap between label and selected menu item
    "& .MuiTypography-root": {
        height: convertPxToRem(20),
        position: !small && "relative",
        top: !small && convertPxToRem(-2),
    },
}));
const StyledMenuItem = styled(MUIMenuItem)(({ theme }) => ({
    "&.MuiMenuItem-root": Object.assign(Object.assign({}, theme.typography.largeText), { height: convertPxToRem(44), "&:hover": {
            backgroundColor: theme.palette.treezGreen[3],
        }, "&.Mui-selected": {
            backgroundColor: theme.palette.treezGreen[3],
        }, "&:focus-visible": {
            backgroundColor: "transparent",
            border: `${convertPxToRem(2.5)} solid ${theme.palette.primaryBlack.main}`,
        } }),
}));
const StyledIconItem = styled(Typography)(() => ({
    display: "flex",
    alignItems: "center",
    gap: convertPxToRem(10),
}));
const StyledFormHelperText = styled(MUIFormHelperText)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.smallText), { color: theme.palette.secondaryText.main, marginTop: convertPxToRem(4), "&.Mui-error": {
        color: theme.palette.error.main,
    }, "&.Mui-disabled": {
        color: theme.palette.tertiaryText.main,
    } })));
const MenuProps = {
    anchorOrigin: {
        horizontal: "center",
        vertical: "bottom",
    },
    transformOrigin: {
        horizontal: "center",
        vertical: "top",
    },
    sx: {
        // the flyout
        "div:nth-child(3)": {
            marginTop: convertPxToRem(2),
        },
        // paper = background of the flyout
        "& .MuiPopover-paper": {
            backgroundColor: lightGreyColors.grey02.main,
            boxShadow: `0 ${convertPxToRem(30)} ${convertPxToRem(40)} rgba(0, 0, 0, 0.12)`,
            borderRadius: convertPxToRem(17),
        },
    },
    disableAutoFocusItem: true,
    MenuListProps: {
        sx: {
            width: "100%",
            padding: `${convertPxToRem(10)} 0`,
        },
    },
};
export const Select = ({ disabled = false, error = false, helperText, label, menuItems = [], onChange, required = false, small = false, testId, value, }) => (_jsxs(StyledFormControl, Object.assign({ disabled: disabled, error: error, required: required, size: small ? "small" : undefined, variant: "filled", fullWidth: true }, { children: [_jsx(StyledInputLabel, Object.assign({ id: label, "data-testid": testId && `label-${testId}`, small: small }, { children: label })), _jsx(StyledSelect, Object.assign({ "data-testid": testId, labelId: label, label: label, onChange: onChange, value: value, MenuProps: MenuProps, disableUnderline: true, tabIndex: 0, small: small }, { children: menuItems.map((item) => {
                var _a;
                return (_jsx(StyledMenuItem, Object.assign({ value: item.displayValue }, { children: _jsxs(StyledIconItem, { children: [((_a = item.iconProps) === null || _a === void 0 ? void 0 : _a.iconName) && (_jsx(Icon, Object.assign({}, item.iconProps, { fontSize: "medium" }))), item.displayName] }) }), item.displayValue));
            }) })), _jsx(StyledFormHelperText, Object.assign({ "data-testid": testId && `helper-${testId}` }, { children: helperText }))] })));
